import React from 'react';
import toast from 'react-hot-toast';
import { liveChallengesApi } from '@/services/liveChallenges';
import { useLiveChallenge } from '@/hooks/useLiveChallenge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const MEDAL_BG = ['bg-[#FFD700]', 'bg-neutral-300', 'bg-[#CD7F32]', 'bg-neutral-lightest'];

export default function QuestionResultView({ challengeId, isLastQuestion, onNextQuestion }) {
  const { questionResult, currentQuestion } = useLiveChallenge();

  const alternatives = currentQuestion?.['live-challenge-alternatives'] || currentQuestion?.live_challenge_alternatives || currentQuestion?.alternatives || [];
  const distribution = questionResult?.answerDistribution || [];
  const correctId = questionResult?.correctAlternativeId;
  const topLeaderboard = (questionResult?.leaderboard || []).slice(0, 5);

  const maxCount = Math.max(...distribution.map(d => d.count || 0), 1);

  const getCountForAlternative = altId => {
    const entry = distribution.find(d => d.alternative_id === altId);
    return entry?.count || 0;
  };

  const handleNext = async () => {
    try {
      await liveChallengesApi.revealQuestion(challengeId);
      onNextQuestion();
    } catch {
      toast.error('Erro ao revelar próxima questão.');
    }
  };

  const handleFinish = async () => {
    try {
      await liveChallengesApi.finish(challengeId);
    } catch {
      toast.error('Erro ao finalizar desafio.');
    }
  };

  return (
    <div className="grid grid-cols-1 gap-8 py-8 md:grid-cols-2 md:gap-12">
      {/* Coluna 1: Questão + distribuição de respostas */}
      <div className="flex flex-col gap-6">
        <Card
          border
          orientation="vertical"
          className="px-6 py-6 text-center"
        >
          <h2 className="m-0 text-xl font-bold text-foreground">{currentQuestion?.description || ''}</h2>
        </Card>

        <div className="flex flex-col gap-3">
          <h3 className="m-0 text-lg font-semibold text-foreground">Distribuição de Respostas</h3>
          {alternatives.map((alt, idx) => {
            const count = getCountForAlternative(alt.id);
            const barWidth = maxCount > 0 ? (count / maxCount) * 100 : 0;
            const isCorrect = alt.id === correctId;

            return (
              <div
                key={alt.id || idx}
                className="flex flex-col gap-1"
              >
                <div className="flex items-center justify-between text-sm">
                  <span className={`font-semibold ${isCorrect ? 'text-success' : 'text-neutral-dark'}`}>
                    {String.fromCharCode(65 + idx)}. {alt.description}
                    {isCorrect && ' (correta)'}
                  </span>
                  <span className="text-neutral">{count}</span>
                </div>
                <div className="h-6 w-full overflow-hidden rounded-full bg-neutral-lightest">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ease-out-strong ${isCorrect ? 'bg-success' : 'bg-neutral-light'}`}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Coluna 2: Top 5 ranking + ação */}
      <div className="flex flex-col gap-6">
        {topLeaderboard.length > 0 ? (
          <div className="flex flex-1 flex-col">
            <h3 className="mb-3 text-lg font-semibold text-foreground">Top 5</h3>
            <Card
              border
              orientation="vertical"
              className="px-0 py-0"
            >
              <ul className="m-0 list-none pl-0">
                {topLeaderboard.map((entry, idx) => {
                  const medalBg = idx < 3 ? MEDAL_BG[idx] : MEDAL_BG[3];
                  const medalText = idx < 3 ? 'text-white' : 'text-neutral-dark';
                  return (
                    <li
                      key={entry.user_id || idx}
                      className="flex items-center justify-between border-b border-solid border-neutral-lightest px-4 py-3 last:border-0"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${medalBg} ${medalText}`}>{idx + 1}</span>
                        <span className="text-sm font-medium text-foreground">{entry.user_name || entry.name || `Participante ${idx + 1}`}</span>
                      </div>
                      <span className="text-sm font-bold text-primary">{entry.total_score ?? entry.score ?? 0} pts</span>
                    </li>
                  );
                })}
              </ul>
            </Card>
          </div>
        ) : (
          <p className="m-0 text-sm text-neutral-light">Aguardando ranking...</p>
        )}

        {isLastQuestion ? (
          <Button
            type="button"
            variant="success"
            size="lg"
            fullWidth
            onClick={handleFinish}
          >
            Finalizar Desafio
          </Button>
        ) : (
          <Button
            type="button"
            variant="default"
            size="lg"
            fullWidth
            onClick={handleNext}
          >
            Próxima Questão
          </Button>
        )}
      </div>
    </div>
  );
}
